import {
  BankAccountDetails as BankAccountDetailsProto,
  BankAccountType as BankAccountTypeProto,
  CheckTaxRefundDeliveryMethod as CheckTaxRefundDeliveryMethodProto,
  DirectDepositTaxRefundDeliveryMethod as DirectDepositTaxRefundDeliveryMethodProto,
  FutureTaxCreditTaxRefundDeliveryMethod as FutureTaxCreditTaxRefundDeliveryMethodProto,
  TaxRefundDeliveryMethod as TaxRefundDeliveryMethodProto,
  type TaxRefundMethod as TaxRefundMethodProto,
  TaxRefundType as TaxRefundTypeProto,
} from '@taxfyle/api-internal/internal/tax_refund_methods_pb'
import { safeTimestampToDate } from '@/utils/grpc-util'
import type {
  TaxRefundMethodVersionProps,
  TaxRefundType,
} from './TaxRefundMethod'
import type {
  CheckTaxRefundDeliveryMethod,
  DirectDepositTaxRefundDeliveryMethod,
  FutureTaxCreditTaxRefundDeliveryMethod,
  TaxRefundDeliveryMethod,
} from './TaxRefundDeliveryMethod'
import type { BankAccountDetails, BankAccountType } from './BankAccount'
import {
  mapPhysicalAddressToProto,
  mapProtoToPhysicalAddress,
} from '@/features/tax-profile/physical-address-mapper'

export function mapTaxRefundMethodProtoToProps(
  proto: TaxRefundMethodProto,
): TaxRefundMethodVersionProps {
  const deliveryMethodProto = proto.getDeliveryMethod()
  return {
    id: proto.getId(),
    version: proto.getVersion(),
    workspaceId: proto.getWorkspaceId(),
    ownerUserId: proto.getOwnerUserId(),
    refundType: mapTaxRefundTypeProtoToProps(proto.getRefundType()),
    deliveryMethod: deliveryMethodProto
      ? mapTaxRefundDeliveryMethodProtoToProps(deliveryMethodProto)
      : { type: 'Check', mailingAddress: null },
    createdAt: safeTimestampToDate(proto.getCreateTime()),
    updatedAt: safeTimestampToDate(proto.getUpdateTime()),
  }
}

export function mapTaxRefundDeliveryMethodProtoToProps(
  proto: TaxRefundDeliveryMethodProto,
): TaxRefundDeliveryMethod {
  switch (proto.getTypeCase()) {
    case TaxRefundDeliveryMethodProto.TypeCase.CHECK:
      return mapCheckTaxRefundDeliveryMethodProtoToProps(proto.getCheck()!)
    case TaxRefundDeliveryMethodProto.TypeCase.DIRECT_DEPOSIT:
      return mapDirectDepositTaxRefundDeliveryMethodProtoToProps(
        proto.getDirectDeposit()!,
      )
    case TaxRefundDeliveryMethodProto.TypeCase.FUTURE_TAX_CREDIT:
      return mapFutureTaxCreditTaxRefundDeliveryMethodProtoToProps(
        proto.getFutureTaxCredit()!,
      )
    default:
      return { type: 'Check', mailingAddress: null }
  }
}

export function mapCheckTaxRefundDeliveryMethodProtoToProps(
  proto: CheckTaxRefundDeliveryMethodProto,
): CheckTaxRefundDeliveryMethod {
  const mailingAddressProto = proto.getMailingAddress()
  return {
    type: 'Check',
    mailingAddress: mailingAddressProto
      ? mapProtoToPhysicalAddress(mailingAddressProto)
      : null,
  }
}

export function mapDirectDepositTaxRefundDeliveryMethodProtoToProps(
  proto: DirectDepositTaxRefundDeliveryMethodProto,
): DirectDepositTaxRefundDeliveryMethod {
  const bankAccountProto = proto.getBankAccount()
  return {
    type: 'DirectDeposit',
    bankAccount: bankAccountProto
      ? mapBankAccountDetailsProtoToProps(bankAccountProto)
      : { accountType: 'Checking', accountNumber: '', routingNumber: '' },
  }
}

export function mapFutureTaxCreditTaxRefundDeliveryMethodProtoToProps(
  _proto: FutureTaxCreditTaxRefundDeliveryMethodProto,
): FutureTaxCreditTaxRefundDeliveryMethod {
  return { type: 'FutureTaxCredit' }
}

export function mapBankAccountDetailsProtoToProps(
  proto: BankAccountDetailsProto,
): BankAccountDetails {
  return {
    accountType: mapBankAccountTypeProtoToProps(proto.getAccountType()),
    accountNumber: proto.getAccountNumber(),
    routingNumber: proto.getRoutingNumber(),
  }
}

export function mapTaxRefundTypeProtoToProps(
  proto: TaxRefundTypeProto,
): TaxRefundType {
  switch (proto) {
    case TaxRefundTypeProto.PERSONAL:
      return 'Personal'
    case TaxRefundTypeProto.BUSINESS:
      return 'Business'
    default:
      return 'Personal'
  }
}

export function mapBankAccountTypeProtoToProps(
  proto: BankAccountTypeProto,
): BankAccountType {
  switch (proto) {
    case BankAccountTypeProto.CHECKING:
      return 'Checking'
    case BankAccountTypeProto.SAVINGS:
      return 'Savings'
    default:
      return 'Checking'
  }
}

export function mapTaxRefundTypeToProto(
  type: TaxRefundType,
): TaxRefundTypeProto {
  switch (type) {
    case 'Personal':
      return TaxRefundTypeProto.PERSONAL
    case 'Business':
      return TaxRefundTypeProto.BUSINESS
    default:
      return TaxRefundTypeProto.TAX_REFUND_TYPE_UNSPECIFIED
  }
}

export function mapTaxRefundDeliveryMethodToProto(
  method: TaxRefundDeliveryMethod,
): TaxRefundDeliveryMethodProto {
  const proto = new TaxRefundDeliveryMethodProto()
  switch (method.type) {
    case 'Check':
      proto.setCheck(mapCheckTaxRefundDeliveryMethodToProto(method))
      break
    case 'DirectDeposit':
      proto.setDirectDeposit(
        mapDirectDepositTaxRefundDeliveryMethodToProto(method),
      )
      break
    case 'FutureTaxCredit':
      proto.setFutureTaxCredit(
        mapFutureTaxCreditTaxRefundDeliveryMethodToProto(method),
      )
      break
  }
  return proto
}

export function mapCheckTaxRefundDeliveryMethodToProto(
  method: CheckTaxRefundDeliveryMethod,
): CheckTaxRefundDeliveryMethodProto {
  const proto = new CheckTaxRefundDeliveryMethodProto()
  if (method.mailingAddress) {
    proto.setMailingAddress(mapPhysicalAddressToProto(method.mailingAddress))
  }
  return proto
}

export function mapDirectDepositTaxRefundDeliveryMethodToProto(
  method: DirectDepositTaxRefundDeliveryMethod,
): DirectDepositTaxRefundDeliveryMethodProto {
  const proto = new DirectDepositTaxRefundDeliveryMethodProto()
  proto.setBankAccount(mapBankAccountDetailsToProto(method.bankAccount))
  return proto
}

export function mapFutureTaxCreditTaxRefundDeliveryMethodToProto(
  _method: FutureTaxCreditTaxRefundDeliveryMethod,
): FutureTaxCreditTaxRefundDeliveryMethodProto {
  return new FutureTaxCreditTaxRefundDeliveryMethodProto()
}

export function mapBankAccountDetailsToProto(
  details: BankAccountDetails,
): BankAccountDetailsProto {
  return new BankAccountDetailsProto()
    .setAccountType(mapBankAccountTypeToProto(details.accountType))
    .setAccountNumber(details.accountNumber)
    .setRoutingNumber(details.routingNumber)
}

export function mapBankAccountTypeToProto(
  type: BankAccountType,
): BankAccountTypeProto {
  switch (type) {
    case 'Checking':
      return BankAccountTypeProto.CHECKING
    case 'Savings':
      return BankAccountTypeProto.SAVINGS
    default:
      return BankAccountTypeProto.BANK_ACCOUNT_TYPE_UNSPECIFIED
  }
}
